import { createSlice } from '@reduxjs/toolkit';

import { createSelector, createSliceHook, makeActionCreator } from '../helpers';

import {
  ReaderTaskState,
  ReaderTaskFetchAction,
  ReaderTaskSuccessAction,
  ReaderTaskSaveAction,
  ReaderTaskCompleteAction,
  ReaderTaskStartAction,
  ReaderTaskSaveOrCompleteSuccessAction,
  ReadingDicomStudiesSuccessAction,
} from './readerTask.types';

const initialState: ReaderTaskState = {
  data: null,
  readingDicomStudies: [],
  readingDicomStudiesTaskId: null,
  DICOMResource: null,
  loading: true,
  saving: false,
};

const { act, actReject, actReq, actResolve, actEmpty } =
  makeActionCreator<ReaderTaskState>();

export const readerTaskSlice = createSlice({
  name: 'readerTask',
  initialState,
  reducers: {
    fetchReaderTask: actReq<ReaderTaskFetchAction['payload']>(),
    fetchReaderTaskSuccess: act<ReaderTaskSuccessAction['payload']>(state => ({
      data: state,
      loading: false,
    })),
    fetchReaderTaskFailure: actReject(),
    resetReaderTask: act(() => ({ ...initialState })),

    /**
     * The grouped reading's studies are fetched beside the task, not instead of it
     *
     * Plain terms
     * -----------
     * Asking for the reading's images must never make the app look like it is
     * still loading the reading itself, and a failure to get them must not stop
     * the viewer from opening on the visit being scored.
     *
     * Technical
     * ---------
     * Written explicitly rather than through `actReq`/`actResolve`/`actReject`
     * because those own `loading` and `error`, which belong to the task fetch.
     * A failure empties the list on purpose: an empty list is the signal the
     * payload selector reads to fall back to the single-visit path (3.9).
     */
    fetchReadingDicomStudies: actEmpty<ReaderTaskFetchAction['payload']>(),
    fetchReadingDicomStudiesSuccess: act<
      ReadingDicomStudiesSuccessAction['payload']
    >(payload => ({
      readingDicomStudies: payload?.studies ?? [],
      readingDicomStudiesTaskId: payload?.taskId ?? null,
      readingDicomStudiesError: undefined,
    })),
    fetchReadingDicomStudiesFailure: act<Error>(payload => ({
      readingDicomStudies: [],
      readingDicomStudiesTaskId: null,
      readingDicomStudiesError: payload,
    })),

    /**
     * Forget the previous reading's studies before the next one is asked for
     *
     * Plain terms
     * -----------
     * Moving from a reading that spans several visits to an ordinary one must
     * not leave the previous reading's images behind: a reading with no images
     * of its own has to say "No data to display", not open somebody else's.
     *
     * Technical
     * ---------
     * `resetReaderTask` only runs when the page unmounts, and an ordinary
     * reading never fetches `dicom/series` at all, so nothing else would
     * overwrite the list. `fetchReaderNeededDataWorker` dispatches this
     * whenever the reading it is opening is not the one the studies were
     * fetched for (D7-7712, C17).
     */
    resetReadingDicomStudies: act(() => ({
      readingDicomStudies: [],
      readingDicomStudiesTaskId: null,
      readingDicomStudiesError: undefined,
    })),

    startReaderTask: actReq<ReaderTaskStartAction['payload']>('saving'),
    startReaderTaskSuccess: actResolve<string>(null, 'saving'),
    startReaderTaskFailure: actReject('savingError', 'saving'),

    fetchReaderDICOMResource: actReq<{
      clinicalTrialId: string;
      endpoint?: string;
    }>('saving'),
    fetchReaderDICOMResourceSuccess: actResolve<string>(
      'DICOMResource',
      'saving'
    ),
    fetchReaderDICOMResourceFailure: actReject('savingError', 'saving'),

    saveReaderTask: actReq<ReaderTaskSaveAction['payload']>('saving'),
    saveReaderTaskSuccess: actResolve<
      ReaderTaskSaveOrCompleteSuccessAction['payload']
    >(null, 'saving'),
    saveReaderTaskFailure: actReject('savingError', 'saving'),

    completeReaderTask: actReq<ReaderTaskCompleteAction['payload']>('saving'),
    completeReaderTaskSuccess: actResolve<
      ReaderTaskSaveOrCompleteSuccessAction['payload']
    >(null, 'saving'),
    completeReaderTaskFailure: actReject('savingError', 'saving'),

    reset: () => {
      return initialState;
    },
  },

  selectors: {
    selectState: state => state,
    selectReaderTask: state => state.data,
    selectCurrentVisit: state =>
      state.data?.visits?.find(v => v.visitId === state.data?.visitId),
  },
});

export const {
  fetchReaderTask,
  fetchReaderTaskSuccess,
  fetchReaderTaskFailure,
  completeReaderTask,
  completeReaderTaskSuccess,
  completeReaderTaskFailure,
  startReaderTask,
  startReaderTaskSuccess,
  startReaderTaskFailure,
  fetchReaderDICOMResource,
  fetchReaderDICOMResourceSuccess,
  fetchReaderDICOMResourceFailure,
} = readerTaskSlice.actions;

export const actions = { ...readerTaskSlice.actions };

export const useReaderTaskSlice = createSliceHook(
  readerTaskSlice.name,
  actions
);

export const selectReaderTaskState = createSelector('readerTask');

export const selectReadingDicomStudies = (s: {
  readerTask: ReaderTaskState;
}): ReaderTaskState['readingDicomStudies'] =>
  s.readerTask?.readingDicomStudies ?? [];
